import React, { useEffect, useMemo, useRef } from "react";
import {
  GoogleMap,
  Marker,
  Circle,
  useJsApiLoader,
  InfoWindow,
} from "@react-google-maps/api";
import { Spin } from "antd";

import { GOOGLE_MAP_LIBRARIES } from "../../../../../constants/googleMaps";
import { useState } from "react";

const DEFAULT_CENTER = {
  lat: 25.2048,
  lng: 55.2708,
};

const mapContainerStyle = {
  width: "100%",
  height: "calc(100vh - 180px)",
};

const mapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  streetViewControl: false,
  fullscreenControl: true,
  mapTypeControl: false,
};

const DeliveryMap = ({ points = [], currentPoint, driverLocation }) => {
  const [selectedPoint, setSelectedPoint] = useState(null);
  const mapRef = useRef(null);

  const mapApiKey =
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    process.env.VITE_GOOGLE_MAPS_API_KEY;

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: mapApiKey,
    libraries: GOOGLE_MAP_LIBRARIES,
  });

  const center = useMemo(() => {
    if (driverLocation?.lat && driverLocation?.lng) {
      return driverLocation;
    }

    if (currentPoint?.latitude && currentPoint?.longitude) {
      return {
        lat: Number(currentPoint.latitude),
        lng: Number(currentPoint.longitude),
      };
    }

    return DEFAULT_CENTER;
  }, [driverLocation, currentPoint]);

  useEffect(() => {
    if (!mapRef.current) return;

    if (!window.google) return;

    const bounds = new window.google.maps.LatLngBounds();

    if (driverLocation?.lat && driverLocation?.lng) {
      bounds.extend(driverLocation);
    }

    points.forEach((point) => {
      if (point.latitude && point.longitude) {
        bounds.extend({
          lat: Number(point.latitude),
          lng: Number(point.longitude),
        });
      }
    });

    if (!bounds.isEmpty()) {
      mapRef.current.fitBounds(bounds);
    }
  }, [driverLocation, points]);


  const getPointColor = (point) => {
    if (point.visit_status === "delivered") {
      return "#52c41a";
    }

    if (point.is_current) {
      return "#1677ff";
    }

    if (point.visit_status === "arrived") {
      return "#fa8c16";
    }

    return "#8c8c8c";
  };

  if (!isLoaded) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: mapContainerStyle.height,
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={center}
      zoom={15}
      options={mapOptions}
      onLoad={(map) => {
        mapRef.current = map;
      }}
    >
      {/* Driver */}

      {driverLocation?.lat && driverLocation?.lng && (
        <Marker position={driverLocation} 
        title="Driver"
        icon={{
            url:
              "https://maps.google.com/mapfiles/kml/shapes/truck.png",
            scaledSize:
              new window.google.maps.Size(40,40),
        }}        
       />
      )}

      {selectedPoint && (

      <InfoWindow
          position={{
              lat:Number(selectedPoint.latitude),
              lng:Number(selectedPoint.longitude),
          }}
          onCloseClick={() =>
              setSelectedPoint(null)
          }
      >

      <div style={{ minWidth: 220 }}>

          <strong>{selectedPoint.name}</strong>

          <br />

          <small>{selectedPoint.address}</small>

          <br />
          <br />

          <b>Status:</b> {selectedPoint.visit_status || "-"}

      </div>

      </InfoWindow>

      )}

      {/* Route Points */}

      {points.map((point) => {
        const isCurrent = point.route_point_id === currentPoint?.route_point_id;

        const position = {
          lat: Number(point.latitude),
          lng: Number(point.longitude),
        };

        return (
          <React.Fragment key={point.route_point_id}>
            <Marker
                position={position}
                title={point.name}
                onClick={() => setSelectedPoint(point)}
                label={{
                    text: `${point.sequence_no}`,
                    color: "#fff",
                    fontWeight: "bold",
                }}
                icon={{
                    path: window.google.maps.SymbolPath.CIRCLE,
                    fillColor: getPointColor(point),
                    fillOpacity: 1,
                    strokeColor: "#ffffff",
                    strokeWeight: 2,
                    scale: point.is_current ? 11 : 8,
                }}
            />

            <Circle
              center={position}
              radius={Number(point.radius_meters) || 100}
              options={{
                  fillColor: getPointColor(point),
                  fillOpacity: point.is_current ? 0.25 : 0.12,
                  strokeColor: getPointColor(point),
                  strokeWeight: point.is_current ? 3 : 1.5,
                  clickable: false,
              }}
            />
          </React.Fragment>
        );
      })}
    </GoogleMap>
  );
};

export default DeliveryMap;
